import { centerNow } from './clock'

/*
 * Formatting for the network console (design/network-console/proto/src/lib/format.ts).
 * The caller renders numbers in mono with tabular figures. Times are the
 * viewer's zone (the prototype used UTC for its fixed clock).
 */

export const ms = (v: number | null | undefined) => (v == null ? '—' : v >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${Math.round(v)} ms`)

/** Sub-millisecond steps of a frame trace: "0.4 ms", "41.6 ms". */
export const ms1 = (v: number | null | undefined) => (v == null ? '—' : Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(2)} s` : `${v.toFixed(1)} ms`)

export const pct = (v: number | null | undefined, digits = 1) => (v == null ? '—' : `${v.toFixed(digits)}%`)

export function bps(v: number | null | undefined): string {
  if (v == null) return '—'
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} Gb/s`
  if (v >= 1e6) return `${(v / 1e6).toFixed(v >= 1e8 ? 0 : 1)} Mb/s`
  if (v >= 1e3) return `${Math.round(v / 1e3)} kb/s`
  return `${v} b/s`
}

const pad = (n: number) => String(n).padStart(2, '0')

/** HH:MM:SS in the viewer's zone. */
export function clock(iso?: string | number | null): string {
  if (iso == null || iso === '') return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/** "now", "12 s ago", "4 min ago", "3 h ago", "2 d ago" — on the center's clock. */
export function ago(iso?: string | null, now = centerNow()): string {
  if (!iso) return '—'
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000))
  if (s < 5) return 'now'
  if (s < 60) return `${s} s ago`
  if (s < 3600) return `${Math.floor(s / 60)} min ago`
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`
  return `${Math.floor(s / 86400)} d ago`
}

/** "1 h 58 min", "4 min 12 s", "9 s". */
export function dur(fromIso?: string | null, now = centerNow()): string {
  if (!fromIso) return '—'
  const s = Math.max(0, Math.round((now - Date.parse(fromIso)) / 1000))
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h ? `${h} h ${m} min` : m ? `${m} min ${s % 60} s` : `${s} s`
}

/** "a1b2 c3d4 … 9f0e": the first 8 and the last 4 hex digits of an SPKI hash. */
export const spkiShort = (hex: string) => `${hex.slice(0, 4)} ${hex.slice(4, 8)} … ${hex.slice(-4)}`

/** "a1b2 c3d4 e5f6 …": the whole hash in groups of four, for reading aloud. */
export const spkiGroups = (hex: string) => hex.replace(/(.{4})(?=.)/g, '$1 ')

/** A 64-bit sequence number with thin spaces every 3 digits: 1 204 880. */
export const seq = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('en-US').replace(/,/g, ' '))

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
